import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 161511 $
//  Purpose   : tree tab parts
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Menu, Image, Tab, Segment, MenuItem } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { funcPart } from 'uix/parts';
import log from 'uix/logging';
import { mapItems, get, size, filterItems, isEqual, isNil, groupBy, sorted } from 'ldash';
import { AutoPortal, HeaderSegment, Link, XPopup } from 'uix/components';
import { VIcon } from 'uix/graphics';
import { getRunId, selectRequestClassCfg } from 'uix/selectors';
import { ucFirst, hashObj } from 'uix/utils';
import { useActions, useEffect, usePartContext, useSelector, useSelectorShallow, useState, useUserSetting, useMemo } from 'uix/hooks';
// ---Local imports
import { useReqTreeNode } from './hooks';
import { rules } from './rules';
import S from './tabs.css';
// ---Globals
const GROUP_CONFIG = {
    overview: {
        order: -10
    },
    list: {
        order: -9
    },
    titles: {
        order: 0
    },
    capsule: {
        icon: 'box'
    },
    job: {
        icon: 'cogs'
    },
    formats: {
        icon: 'clipboard_outline'
    },
    config: {
        icon: 'configure',
        order: 9
    },
    sys: {
        icon: 'server',
        order: 10
    },
    database: {
        icon: 'database',
        order: 11
    },
    transcript: {
        icon: 'transcript'
    },
    user: {
        order: 18,
        icon: 'users'
    },
    lab: {
        order: 19
    },
    debug: {
        order: 20
    },
    workshop: {
        icon: 'workshop'
    },
    // raw class/tab derived groups
    tree_mods: {
        icon: 'edit_outline'
    },
    tree_list: {
        icon: 'list'
    },
    tree_viewer: {
        icon: 'eye'
    },
    text_id: {
        icon: 'cog'
    },
    tree_wiki: {
        icon: 'edit'
    },
    tree_pdf: {
        icon: 'file pdf outline'
    },
    tree_stats: {
        icon: 'bar chart'
    }
};
const buildLabel = (i18n, label, classname, tabname)=>label || i18n.get(`tab${ucFirst(classname)}${ucFirst(tabname)}`, ucFirst(tabname));
export const TabLabel = ({})=>{
    let classCfg = useSelector(selectRequestClassCfg);
    let { classname, tabname } = useSelector(Sel.request);
    let cfg = classCfg.tabs[tabname];
    let i18n = useSelector(Sel.i18n);
    return buildLabel(i18n, cfg.label, classname, tabname);
};
export const MinorTabGroup = ({ className, showSingle = false, bottom, ...props })=>{
    let request = useSelector(Sel.request);
    let classCfg = useSelector(selectRequestClassCfg);
    let { majorTabGrouping, tabGroup } = useAvailableTabs(request, classCfg);
    let { classname, tabname } = request;
    let { i18n, dname } = usePartContext();
    let switchTopTab = useActions(Actions.switchTopTab);
    let minorTabGroup = classCfg.tabs[tabname]?.minorTabGroup;
    if (!minorTabGroup || isNil(majorTabGrouping)) {
        return null;
    }
    if (bottom) {
        props = {
            ...props,
            menu: {
                size: 'small',
                attached: true,
                style: {
                    width: '99.99%'
                }
            }
        };
    }
    let panes = mapItems(classCfg.tabs, (name, cfg)=>({
            menuItem: /*#__PURE__*/ _jsx(Menu.Item, {
                className: S.tabLink,
                as: "div",
                children: /*#__PURE__*/ _jsxs(Link, {
                    tabname: name,
                    children: [
                        cfg.icon && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(VIcon, {
                                    name: cfg.icon
                                }),
                                ' '
                            ]
                        }),
                        buildLabel(i18n, cfg.label, classname, name)
                    ]
                })
            }, name),
            tabname: name
        }), (name, cfg)=>cfg.minorTabGroup == minorTabGroup && majorTabGrouping.includes(name));
    if (!showSingle && panes.length < 2) {
        return null;
    }
    let activeIndex = panes.findIndex((e)=>e.tabname == tabname);
    let tabChange = (index)=>{
        let nextTab = panes[index].tabname;
        let tabGroup = classCfg.tabs[tabname].tabGroup || classCfg.tabGroup;
        switchTopTab(classname, nextTab, tabGroup, minorTabGroup);
        return false;
    };
    return /*#__PURE__*/ _jsx(Tab, {
        className: className,
        activeIndex: activeIndex,
        panes: panes,
        onTabChange: (e, data)=>tabChange(data.activeIndex),
        ...props
    });
};
export const MinorTabGroupSegment = ({ children, flex = false, header = null, menu, ...props })=>{
    /* build a segment with third level tabs header
   */ let styleSecondary = {
        padding: '0rem'
    };
    if (flex) {
        styleSecondary = {
            ...styleSecondary,
            display: 'flex',
            flex: 1,
            overflow: 'auto',
            flexDirection: 'column'
        };
    }
    return /*#__PURE__*/ _jsxs(HeaderSegment, {
        menu: header && menu,
        major: true,
        flex: flex,
        header: header,
        ...props,
        children: [
            header ? /*#__PURE__*/ _jsx(MinorTabGroup, {
                className: S.minorTabSegment
            }) : /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginRight: '-1rem',
                    marginLeft: 'calc(-1rem - 1px)'
                },
                children: [
                    /*#__PURE__*/ _jsx(MinorTabGroup, {
                        className: S.minorTabSegment
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: S.minorTabMenu,
                        children: menu
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Segment, {
                secondary: true,
                style: styleSecondary,
                children: children
            })
        ]
    });
};
export const MinorBottomTabGroupSegment = ({ children, bare, style = {}, ...props })=>{
    style = {
        ...style,
        marginBottom: 0,
        paddingBottom: 0
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(HeaderSegment, {
                major: true,
                flex: true,
                flexInner: true,
                style: style,
                ...props,
                children: children
            }),
            /*#__PURE__*/ _jsx(MinorTabGroup, {
                bottom: true
            })
        ]
    });
};
const useAvailableTabs = (request, classCfg)=>{
    let { classname, tabname } = request;
    let iconfig = useSelector(Sel.iconfig);
    let i18n = useSelector(Sel.i18n);
    let user = useSelector(Sel.user);
    let { activeNode, allClassCfgs, treeById, treeChildData, treeDetailsById, loadedDomains, chosenTopTabs } = useTabSelector();
    let dname = activeNode.dname || request.dname;
    let classCfgs = allClassCfgs[dname];
    let availableTabs = new Set();
    if (activeNode && classCfgs) {
        let treeRules = rules({
            allClassCfgs,
            treeById,
            treeChildData,
            treeDetailsById,
            iconfig,
            loadedDomains,
            i18n,
            user,
            request
        }, {});
        let rule = treeRules[activeNode.nodeType]?.tabs || (()=>[]);
        availableTabs = new Set(rule(activeNode).map(([classname, tabname])=>`${classname}/${tabname}`));
    }
    if (!classCfg.tabs[tabname]) {
        return {};
    }
    let tabGroup = classCfg.tabs[tabname].tabGroup || classCfg.tabGroup;
    if (!tabGroup) {
        return {};
    }
    // fallback to domain datatype
    let currentDatatype = get(treeById[request.id], 'datatype', 10);
    let is_valid = (classCfg, tab)=>(classCfg.tabGroup === tabGroup || tab.tabGroup === tabGroup) && // tabs without datatype info are always valid for domain contexts
        //~ ((tab.datatype && tab.datatype.includes(currentDatatype)) ||
        //~ (size(tab.datatype) == 0 && currentDatatype == 10)) &&
        availableTabs.has(`${classCfg.name}/${tab.name}`);
    // build list of tabnames
    let groupedTabs = filterItems(classCfg.tabs, (name, tab)=>is_valid(classCfg, tab)).map((e)=>e[0]);
    return {
        majorTabGrouping: groupedTabs,
        tabGroup
    };
};
export const TreeTopTabs = ({ iconfig, request, go, i18n })=>{
    /* part for tabs above center view
  all tabs that are grouped by the tabGroup property
   */ let [nextTabInfo, setNextTabInfo] = useState({
        nextTab: null,
        switchRunId: null
    });
    let { nextTab, switchRunId } = nextTabInfo;
    let chosenMinorTabGroupTabs = useSelector((root)=>root.data.tree.chosenMinorTabGroupTabs);
    let { classname, tabname } = request;
    let switchTopTab = useActions(Actions.switchTopTab);
    let runId = useSelector(getRunId);
    // scan only tabs from this class
    let classCfg = useSelector(selectRequestClassCfg);
    let { majorTabGrouping, tabGroup } = useAvailableTabs(request, classCfg);
    if (isNil(majorTabGrouping)) {
        return null;
    }
    // discard tabs with minorTabGroup, keep only first in list
    let topGrouped = {};
    majorTabGrouping = majorTabGrouping.map((name)=>{
        let cfg = classCfg.tabs[name];
        if (!cfg.minorTabGroup) {
            return name;
        }
        let groupedTabs = topGrouped[cfg.minorTabGroup];
        if (groupedTabs) {
            // another tab has already triggered grouping
            topGrouped[cfg.minorTabGroup] = [
                ...groupedTabs,
                name
            ];
            return null;
        } else {
            // XXX: check if cfg.minorTabGroup clashes with tabnames?
            topGrouped[cfg.minorTabGroup] = [
                name
            ];
            // first tab with minorTabGroup remains in list
            return cfg.minorTabGroup;
        }
    }).filter((e)=>e); // filter nulls
    // determine active tab or group name
    let minorTabGroup = classCfg.tabs[tabname].minorTabGroup;
    let activeTabname = minorTabGroup ? minorTabGroup : tabname;
    if (nextTab && switchRunId === runId) {
        activeTabname = nextTab;
    }
    // only show tabs if more than one
    if (majorTabGrouping.length < 2) {
        return null;
    }
    let activeIndex = majorTabGrouping.indexOf(activeTabname);
    let panes = majorTabGrouping.map((name, index)=>{
        let tabname = majorTabGrouping[index];
        if (topGrouped[tabname]) {
            let k = `${tabGroup}/${tabname}`;
            // reuse choice if previously chosen and in available majorTabGrouping
            if (chosenMinorTabGroupTabs[k] && majorTabGrouping[chosenMinorTabGroupTabs[k][1]]) {
                // take tab of classname, tabname tupel
                tabname = chosenMinorTabGroupTabs[k][1];
            } else {
                // take first
                tabname = topGrouped[tabname][0];
            }
        }
        let cfg = classCfg.tabs[tabname];
        return /*#__PURE__*/ _jsx(Link, {
            request: {
                classname,
                tabname
            },
            className: clsx('item', activeIndex == index && 'active'),
            onClick: (request)=>{
                setNextTabInfo({
                    nextTab: request.tabname,
                    switchRunId: runId
                });
                switchTopTab(request.classname, request.tabname, tabGroup);
                return request;
            },
            children: buildLabel(i18n, cfg.label, classname, name)
        }, name);
    });
    return /*#__PURE__*/ _jsx("div", {
        className: S.topTabs,
        children: /*#__PURE__*/ _jsx(Menu, {
            secondary: true,
            pointing: true,
            children: panes
        })
    });
};
const getTabGroups = ({ node, reqTab, treeRules, classCfgs, i18n, chosenTopTabs })=>{
    /* determine tabs (use vievs/tabs enabled() and tree.rules.rule.tabs)
  and group into tabGroups for node/request
   */ let rule = treeRules[node.nodeType];
    // chosen by group
    let chosenTabs = chosenTopTabs[node.nodeType] || {};
    let _get = ([classname, tabname])=>classCfgs[classname]?.tabs[tabname];
    // get all tabs for this node (datatype/details filtered for vltype nodes)
    let tabrules = get(rule, 'tabs', ()=>[]);
    let tabs = tabrules(node);
    // filter down if some are not available in classcfgs (auth lost case)
    tabs = tabs.filter((t)=>_get(t));
    let _group = ([classname, tabname])=>_get([
            classname,
            tabname
        ])?.tabGroup || classCfgs[classname]?.tabGroup;
    // there are grouped tabs with filtered (datatype) explicit tabGroupDefault
    // assume a new tabGroupDefault for these groups
    let byTg = groupBy(tabs, (t)=>_group(t));
    let byTgNoDefaults = filterItems(byTg, (name, tabs)=>name !== 'undefined' && !tabs.some((t)=>_get(t).tabGroupDefault));
    let newTabGroupDefaults = byTgNoDefaults.map(([tabGroup, tabs])=>_get(tabs[0]));
    let group_is_chosen = [];
    // remove tabs that are grouped but not chosen or default
    let classify = (tab)=>{
        let group = _group(tab);
        if (!group) {
            // ungrouped tabs remain in list
            return [
                'ungrouped',
                tab
            ];
        }
        // XXX: probably not a good idea
        // we need to filter duplicate groups now below
        if (isEqual(reqTab, tab)) {
            return [
                'requested',
                tab,
                group
            ];
        }
        // manually chosen, can give duplicates with defaults
        if (isEqual(chosenTabs[group], tab)) {
            group_is_chosen.push(group);
            return [
                'chosen',
                tab,
                group
            ];
        }
        let is_default = _get(tab).tabGroupDefault || newTabGroupDefaults.includes(_get(tab));
        if (is_default) {
            return [
                'default',
                tab,
                group
            ];
        }
    };
    let classified_tabs = tabs.map((tab)=>classify(tab)).filter((e)=>e);
    // remove default variant if chosen variant is available
    classified_tabs = classified_tabs.filter(([type, tab, group])=>type !== 'default' || !group_is_chosen.includes(group));
    tabs = classified_tabs.map(([type, tab])=>tab);
    let getLabel = (tab)=>{
        let [classname, tabname] = tab;
        if (_group(tab)) {
            return i18n.get(`tabgroup${ucFirst(_group(tab))}`);
        }
        return i18n.get(`tab${ucFirst(classname)}${ucFirst(tabname)}`, classname !== 'tree' ? ucFirst(classname) : ucFirst(tabname));
    };
    let reqGroup = _group(reqTab);
    let addedGroups = new Set() // filter duplicate groups (due to classify)
    ;
    let tabGroups = tabs.map((tab)=>{
        let group = _group(tab);
        if (addedGroups.has(group)) return;
        addedGroups.add(group);
        return {
            group,
            tab,
            label: getLabel(tab),
            // does this group belongs to the request class/tab
            active: !isNil(group) && group == reqGroup || isEqual(reqTab, tab)
        };
    }).filter((e)=>e);
    return tabGroups;
};
const useTabSelector = ()=>{
    let res = useSelectorShallow((root)=>({
            treeById: root.data.tree.byId,
            treeDetailsById: root.data.tree.detailsById,
            treeChildData: root.data.tree.childData,
            activeNode: root.data.tree.activeNode,
            allClassCfgs: Sel.allDomainClassCfgs(root),
            loadedDomains: root.config.ddata,
            chosenTopTabs: root.data.tree.chosenTopTabs
        }));
    let reqNode = useReqTreeNode();
    //ok, here's the catch:
    // activeNode switches immediately and triggers a go-action
    // which resolves a new request in the future
    // tabs should be stable during navigation and read the new activeNode
    // after new request is active (maybe depend only on runId?)
    let request = useSelector(Sel.request);
    let [usedActiveNode, setUsedActiveNode] = useState(res.activeNode);
    useEffect(()=>{
        if (res.activeNode !== usedActiveNode) {
            setUsedActiveNode(res.activeNode);
        }
    }, [
        request
    ]);
    // XXX: broken
    usedActiveNode = res.activeNode;
    res = useMemo(()=>{
        // 2020-11-01: tabs are now more and more dependent on nodeType, so we also prefer activeNode here (as in MajorTabGroup)
        // this means we should probably not use stuff like treeDetailData for tab-eval (but only more static config values)
        return {
            ...res,
            activeNode: usedActiveNode || reqNode
        };
    }, [
        res,
        usedActiveNode,
        reqNode
    ]);
    return res;
};
export const MajorTabGroup = funcPart(({ request, go, i18n, iconfig, user })=>{
    let { activeNode, allClassCfgs, treeById, treeChildData, treeDetailsById, loadedDomains, chosenTopTabs } = useTabSelector();
    let switchTabGroup = useActions(Actions.switchTabGroup);
    let dname = activeNode.dname || request.dname;
    let classCfgs = allClassCfgs[dname];
    let groups = [];
    if (activeNode && classCfgs) {
        let treeRules = rules({
            allClassCfgs,
            treeById,
            treeChildData,
            treeDetailsById,
            iconfig,
            loadedDomains,
            i18n,
            user,
            request
        }, {});
        let reqTab = [
            request.classname,
            request.tabname
        ];
        let tabGroups = getTabGroups({
            node: activeNode,
            reqTab,
            treeRules,
            classCfgs,
            i18n,
            chosenTopTabs
        });
        groups = tabGroups.map(({ label, tab, group, active })=>({
                label,
                onSwitch: ()=>switchTabGroup(activeNode.nodeType, group, tab[0], tab[1]),
                requestParams: {
                    classname: tab[0],
                    tabname: tab[1]
                },
                active,
                group: group || tab.join('_'),
                tab
            }));
    }
    // initialize with dummy group, this is disputable
    let [lastGroups, setLastGroups] = useState([
        {
            label: '...',
            requestParams: {}
        }
    ]);
    useEffect(()=>{
        if (size(groups) > 0) {
            setLastGroups(groups);
        }
    }, [
        hashObj(groups.map((g)=>[
                g.group,
                g.tab
            ]))
    ]);
    return /*#__PURE__*/ _jsx(TabGroups, {
        groups: groups.length > 0 ? groups : lastGroups
    });
});
const TabGroups = ({ groups })=>{
    let [showLabels, setShowLabels] = useState(false);
    let [navBarTarget] = useUserSetting('navBarTarget');
    groups = sorted(groups, ({ group })=>get(GROUP_CONFIG, group, {}).order || 0);
    let extended = false;
    let [position, vertical] = {
        centerLeftPortal: [
            'center',
            true
        ],
        leftPortal: [
            'left',
            true
        ],
        centerRightPortal: [
            'right',
            true
        ],
        topPortal: [
            'top',
            false
        ],
        centerBottomPortal: [
            'bottom',
            false
        ]
    }[navBarTarget] ?? [
        'left',
        true
    ];
    let items = groups.map(({ label, onSwitch, requestParams, active, group }, i)=>{
        let icon = GROUP_CONFIG[group]?.icon || group;
        let labelVisible = showLabels || !vertical;
        let linkItem = /*#__PURE__*/ _jsx(Link, {
            title: label,
            classname: requestParams.classname,
            tabname: requestParams.tabname,
            onClick: (request)=>{
                onSwitch();
                return request;
            },
            children: /*#__PURE__*/ _jsx(Menu.Item, {
                active: active,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: S.menuItem,
                    children: [
                        /*#__PURE__*/ _jsx(VIcon, {
                            name: icon,
                            suiFallback: true
                        }),
                        labelVisible && /*#__PURE__*/ _jsx("span", {
                            className: S.label,
                            children: label
                        })
                    ]
                })
            })
        }, i);
        if (!vertical) {
            return /*#__PURE__*/ _jsx(XPopup, {
                fitted: true,
                content: /*#__PURE__*/ _jsx(Segment, {
                    style: {
                        padding: '0.5rem'
                    },
                    size: "small",
                    inverted: true,
                    children: label
                }),
                basic: true,
                mouseEnterDelay: 400,
                style: {
                    marginTop: '2px'
                },
                position: "top center",
                trigger: linkItem
            }, i);
        } else {
            return linkItem;
        }
    });
    let apiurl = useSelector(Sel.apiurl);
    let favico = apiurl.href(`/favicon.ico`);
    let logo = /*#__PURE__*/ _jsx(Menu.Item, {
        disabled: true,
        className: S.logo,
        children: /*#__PURE__*/ _jsx(Image, {
            src: favico,
            style: {
                height: '2em',
                margin: '0 auto'
            }
        })
    });
    return /*#__PURE__*/ _jsx(AutoPortal, {
        target: navBarTarget,
        children: /*#__PURE__*/ _jsxs(Menu, {
            size: "small",
            vertical: vertical,
            className: clsx(extended ? S.advanced : S.tabGroup, vertical ? S.vertical : S.horizontal, position === 'center' && S.center, position === 'left' && S.left, position === 'right' && S.right, position === 'bottom' && S.bottom, position === 'top' && S.top, showLabels && S.labelsShown),
            children: [
                logo,
                /*#__PURE__*/ _jsx("span", {
                    className: S.floating,
                    onClick: ()=>setShowLabels(!showLabels),
                    children: /*#__PURE__*/ _jsx(VIcon, {
                        name: showLabels ? 'chevron left' : 'chevron right'
                    })
                }),
                items
            ]
        }, navBarTarget)
    });
};
